import type { Aircraft, AircraftIconKind, FlightClass, SpecialFlightKind } from '@flightscanner/shared';
import { airlineFromCallsign, getTypeInfo, inferCountry } from './reference';
import { hasMilitaryEvidence } from './militaryEvidence';

/** Government / state operator callsign prefixes (non-military). */
const GOV_AIRLINES = new Set(['SAM', 'EXEC', 'RFA', 'PAT', 'CFS', 'CG', 'GCC']);

const GOV_OP = /government|state department|presidential|ministry|federal bureau|customs|border patrol|police|gendarmerie/i;
const EMERGENCY_SQUAWKS = new Set(['7700', '7600', '7500']);
const PRESIDENTIAL_CALLSIGN =
  /^(AF0?1|AF0?2|AFO0?1|AFO0?2|AIRFORCE0?1|AIRFORCE0?2|AIRFORCEONE|AIRFORCETWO|AIR FORCE ONE|AIR FORCE TWO)$/i;
/** 89th Airlift Wing fleet callsign when no VIP is aboard (same aircraft as AF1/AF2). */
const PRESIDENTIAL_FLEET_CALLSIGN = /^VENUS\w*/i;
const VIP_CALLSIGN = /^(SAM|SPAR|EXEC|RCH|PAT|BOXER|GRIM|NIGHT|VV|VM)\w*/i;
const PRESIDENTIAL_REG = /^(82-?8000|92-?9000|28000|29000)$/i;

const HEAVY_COMMERCIAL = new Set([
  'A124',
  'A225',
  'A306',
  'A310',
  'A332',
  'A333',
  'A339',
  'A342',
  'A343',
  'A345',
  'A346',
  'A359',
  'A35K',
  'A388',
  'B742',
  'B744',
  'B748',
  'B762',
  'B763',
  'B764',
  'B772',
  'B773',
  'B77L',
  'B77W',
  'B788',
  'B789',
  'B78X',
  'DC10',
  'IL96',
  'L101',
  'MD11',
]);
const MILITARY_FIGHTERS = new Set([
  'A10',
  'A29',
  'ALCA',
  'AV8B',
  'BUC',
  'EUFI',
  'F4',
  'F5',
  'F14',
  'F15',
  'F16',
  'F18',
  'F22',
  'F35',
  'HAWK',
  'HUNT',
  'J10',
  'J11',
  'J15',
  'J16',
  'J20',
  'JAS39',
  'JF17',
  'L39',
  'L159',
  'M200',
  'M346',
  'MIR2',
  'MIR4',
  'PC21',
  'RAFA',
  'SU27',
  'SU30',
  'SU35',
  'T38',
  'T45',
  'T6',
  'TOR',
  'Y130',
]);
const MILITARY_TRANSPORTS = new Set([
  'A400',
  'B742',
  'B752',
  'B763',
  'C130',
  'C135',
  'C17',
  'C5M',
  'E3CF',
  'E6',
  'E7',
  'IL76',
  'KC10',
  'KC30',
  'KC46',
  'K35R',
  'P8',
  'T204',
  'V22',
]);
/** ICAO types that are fixed-wing but can false-match helicopter heuristics. */
const FIXED_WING_OVERRIDES = new Set(['HAWK', 'H25B', 'H25C', 'HUNA', 'HERN']);
/** Known ICAO helicopter type designators (explicit — never use bare "H" prefix). */
const HELICOPTER_TYPES = new Set([
  'A109',
  'A139',
  'A169',
  'A189',
  'AS32',
  'AS50',
  'AS55',
  'AS65',
  'AS72',
  'AW09',
  'AW13',
  'AW19',
  'AW39',
  'AW89',
  'AW139',
  'AW189',
  'B06',
  'B06T',
  'B407',
  'B412',
  'B429',
  'B47',
  'B505',
  'B525',
  'BK17',
  'CH47',
  'CH53',
  'CH54',
  'EC20',
  'EC25',
  'EC30',
  'EC35',
  'EC45',
  'EC55',
  'EC75',
  'EC120',
  'EC130',
  'EC135',
  'EC145',
  'EC155',
  'EC175',
  'EC225',
  'EC665',
  'H130',
  'H145',
  'H160',
  'H175',
  'H215',
  'H225',
  'H47',
  'H53',
  'H55',
  'H60',
  'H64',
  'HH60',
  'KA32',
  'KA52',
  'MH60',
  'MI8',
  'MI17',
  'MI24',
  'MI26',
  'MI28',
  'MI34',
  'NH90',
  'R22',
  'R44',
  'R66',
  'S61',
  'S76',
  'S92',
  'SH60',
  'UH1',
  'UH60',
  'W3',
]);
const MILITARY_FIGHTER_DESC =
  /FIGHTER|F-?\d|TYPHOON|RAFALE|HORNET|VIPER|LIGHTNING II|HAWK|TALON|GOSHAWK|HARRIER|TORNADO|JET TRAINER|TRAINER JET|ADVANCED TRAINER|LIGHT COMBAT|ALCA|M346|L-39|L39|PC-?21/i;
const LIGHT_PERSONAL = /^(C1|C2|C3|C4|C5|C6|C7|C8|C9|C17[025]|C18[025]|C20[568]|C21[012]|C30[356]|P28|PA|SR2|DA4|BE2|BE3|BE4|RV|AA5|AEST|RANS)/;
const BALLOON_TYPE = /^(BALL|BAL|HBAL|SHIP)$/;

function isHelicopterType(type: string, desc: string, typeInfo: ReturnType<typeof getTypeInfo>): boolean {
  const up = type.toUpperCase();
  if (up && FIXED_WING_OVERRIDES.has(up)) return false;
  if (typeInfo?.category === 'helicopter') return true;
  if (/HELICOPTER|ROTORCRAFT|\bHELI\b/.test(desc)) return true;
  if (up && HELICOPTER_TYPES.has(up)) return true;
  return false;
}

export interface RichAircraft extends Aircraft {
  airline: ReturnType<typeof airlineFromCallsign>;
  typeInfo: ReturnType<typeof getTypeInfo>;
  inferredCountry: string | null;
  flightClass: FlightClass;
  iconKind: AircraftIconKind;
  specialKind: SpecialFlightKind;
}

let cache = new Map<string, RichAircraft>();

/**
 * Enrich a raw Aircraft with airline, type info, inferred country, and a
 * flight class used by filtering and map styling. Memoized by ICAO hex; the
 * cache is invalidated when the upstream `seen` value is smaller (fresher).
 */
export function enrich(ac: Aircraft): RichAircraft {
  const prev = cache.get(ac.icao);
  if (
    prev &&
    prev.seen <= ac.seen &&
    prev.callsign === ac.callsign &&
    prev.type === ac.type &&
    prev.squawk === ac.squawk &&
    prev.emergency === ac.emergency &&
    prev.registration === ac.registration &&
    prev.operator === ac.operator
  ) {
    // Reuse enrichment, but copy live numeric fields.
    return {
      ...prev,
      ...ac,
      airline: prev.airline,
      typeInfo: prev.typeInfo,
      inferredCountry: prev.inferredCountry,
      flightClass: prev.flightClass,
      iconKind: prev.iconKind,
      specialKind: prev.specialKind,
    };
  }

  const airline = airlineFromCallsign(ac.callsign);
  const typeInfo = getTypeInfo(ac.type);
  const inferredCountry = inferCountry(ac.registration, airline);
  const op = ac.operator ?? '';
  const military = hasMilitaryEvidence(ac, airline, typeInfo);

  let flightClass: FlightClass;
  if (military) flightClass = 'military';
  else if ((airline && GOV_AIRLINES.has(airline.icao)) || GOV_OP.test(op)) flightClass = 'government';
  else if (
    typeInfo?.category === 'private_jet' ||
    typeInfo?.category === 'light' ||
    typeInfo?.category === 'glider'
  )
    flightClass = 'private';
  else flightClass = 'commercial';

  const specialKind = classifySpecial(ac, op);
  const iconKind = classifyIcon(ac, flightClass, specialKind);
  const rich: RichAircraft = { ...ac, airline, typeInfo, inferredCountry, flightClass, iconKind, specialKind };
  cache.set(ac.icao, rich);
  return rich;
}

function isEmergency(ac: Aircraft): boolean {
  if (ac.squawk && EMERGENCY_SQUAWKS.has(ac.squawk)) return true;
  return Boolean(ac.emergency && ac.emergency !== 'none');
}

function classifySpecial(ac: Aircraft, operator: string): SpecialFlightKind {
  const callsign = (ac.callsign ?? '').trim().toUpperCase().replace(/\s+/g, '');
  const displayCallsign = (ac.callsign ?? '').trim().toUpperCase();
  const registration = (ac.registration ?? '').trim().toUpperCase();
  if (
    PRESIDENTIAL_CALLSIGN.test(callsign) ||
    PRESIDENTIAL_CALLSIGN.test(displayCallsign) ||
    PRESIDENTIAL_FLEET_CALLSIGN.test(callsign) ||
    PRESIDENTIAL_REG.test(registration)
  ) {
    return 'presidential';
  }
  if (VIP_CALLSIGN.test(callsign) || /presidential|executive airlift|special air mission/i.test(operator)) return 'vip';
  return null;
}

function classifyIcon(ac: Aircraft, flightClass: FlightClass, specialKind: SpecialFlightKind): AircraftIconKind {
  if (specialKind === 'presidential') return 'presidential';
  if (isEmergency(ac)) return 'emergency';

  const type = (ac.type ?? '').toUpperCase();
  const desc = (ac.description ?? '').toUpperCase();
  const operator = (ac.operator ?? '').toUpperCase();
  const typeInfo = getTypeInfo(type);

  if (BALLOON_TYPE.test(type) || /BALLOON|WEATHER BALLOON|RADIOSONDE/.test(desc) || /BALLOON/.test(operator)) return 'balloon';

  const helicopter = isHelicopterType(type, desc, typeInfo);

  if (helicopter) {
    return flightClass === 'military' ? 'military-helicopter' : 'helicopter';
  }

  if (flightClass === 'military') {
    if (MILITARY_FIGHTERS.has(type) || MILITARY_FIGHTER_DESC.test(desc)) {
      return 'military-fighter';
    }
    if (MILITARY_TRANSPORTS.has(type) || /TANKER|TRANSPORT|GLOBEMASTER|HERCULES|POSEIDON|STRATOTANKER|AIRLIFTER/.test(desc)) {
      return 'military-transport';
    }
    return 'military-other';
  }

  if (typeInfo?.category === 'glider' || /GLIDER|SAILPLANE/.test(desc)) return 'glider';

  if (specialKind === 'vip' || flightClass === 'government') return 'government';
  if (typeInfo?.category === 'private_jet') return 'private-jet';
  if (typeInfo?.category === 'light' || LIGHT_PERSONAL.test(type)) return 'light-prop';
  if (typeInfo?.category === 'turboprop') return 'turboprop';
  if (typeInfo?.category === 'regional') return 'regional';
  if (HEAVY_COMMERCIAL.has(type)) return 'heavy-commercial';
  if (typeInfo?.category === 'commercial_jet') return 'commercial';
  if (/CESSNA|PIPER|CIRRUS|BEECH|BONANZA|DIAMOND DA|MOONEY|VANS RV/i.test(desc)) return 'light-prop';
  return flightClass === 'private' ? 'private-jet' : 'unknown';
}

export function enrichAll(list: Aircraft[]): RichAircraft[] {
  const result = list.map(enrich);
  // Prune cache entries for aircraft no longer in the current list.
  // Keeps the cache bounded to the active viewport instead of growing forever.
  if (cache.size > list.length + 200) {
    const seen = new Set(list.map((a) => a.icao));
    for (const key of cache.keys()) {
      if (!seen.has(key)) cache.delete(key);
    }
  }
  return result;
}

/** Clear the enrichment cache (e.g. on full reload). */
export function clearEnrichCache(): void {
  cache = new Map();
}
